// src/services/httpClient.js - Production-ready HTTP client with auth token management
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// Create axios instance
const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

const getStoredToken = () => {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
};

const getStoredUser = () => {
  const userData = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
  return userData ? JSON.parse(userData) : null;
};

const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    return payload.exp < now;
  } catch (error) {
    return true;
  }
};

// Request interceptor
httpClient.interceptors.request.use(
  (config) => {
    const token = getStoredToken();

    if (token && !isTokenExpired(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request metadata
    config.headers['X-Request-ID'] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    config.headers['X-Client-Timestamp'] = new Date().toISOString();

    // Fix for FormData uploads: Let axios automatically set Content-Type for FormData
    // If data is FormData, remove Content-Type header so axios can set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with token refresh
httpClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If refresh is in progress, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return httpClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = getStoredToken();
        const user = getStoredUser();

        if (!token || !user) {
          throw new Error('No token or user data available');
        }

        // Try to refresh token using session extension
        const refreshResponse = await axios.post(`${API_BASE_URL}/auth/sessions/extend`, {
          location: await getCurrentLocation().catch(() => null)
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (refreshResponse.data.success && refreshResponse.data.data?.token) {
          const newToken = refreshResponse.data.data.token;
          const newUser = refreshResponse.data.data.user;

          // Update stored data
          const storage = localStorage.getItem('auth_token') ? localStorage : sessionStorage;
          storage.setItem('auth_token', newToken);
          storage.setItem('auth_user', JSON.stringify(newUser));
          storage.setItem('auth_last_activity', Date.now().toString());

          // Process queued requests
          processQueue(null, newToken);

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return httpClient(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        processQueue(refreshError, null);
        clearAuthData();
        
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other error codes
    if (error.response?.status === 403) {
      console.warn('Access forbidden:', error.response.data?.message);
    } else if (error.response?.status === 429) {
      console.warn('Rate limited. Please wait before making more requests.');
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response.status);
    }

    return Promise.reject(error);
  }
);

// Helper functions
const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
      },
      (error) => {
        reject(error);
      },
      {
        timeout: 5000,
        enableHighAccuracy: false,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

const clearAuthData = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_last_activity');
  localStorage.removeItem('auth_remember_me');
  
  sessionStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_user');
  sessionStorage.removeItem('auth_last_activity');
};

// Auth API methods
export const authAPI = {
  async login(credentials) {
    try {
      const response = await httpClient.post('/auth/login/employee', {
        email: credentials.email,
        password: credentials.password,
        location: await getCurrentLocation().catch(() => null)
      });

      if (response.data.success && response.data.data) {
        const { token, user, session } = response.data.data;
        
        // Store based on remember me preference
        const storage = credentials.rememberMe ? localStorage : sessionStorage;
        storage.setItem('auth_token', token);
        storage.setItem('auth_user', JSON.stringify(user));
        storage.setItem('auth_last_activity', Date.now().toString());
        
        if (credentials.rememberMe) {
          localStorage.setItem('auth_remember_me', 'true');
        }

        return { success: true, token, user, session };
      }

      throw new Error(response.data.message || 'Login failed');
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Login failed';
      return { success: false, error: message };
    }
  },

  async loginAdmin(credentials) {
    try {
      const response = await httpClient.post('/auth/login/admin', {
        email: credentials.email,
        password: credentials.password,
        location: await getCurrentLocation().catch(() => null)
      });

      if (response.data.success && response.data.data) {
        const { token, user, session } = response.data.data;
        
        // Store based on remember me preference
        const storage = credentials.rememberMe ? localStorage : sessionStorage;
        storage.setItem('auth_token', token);
        storage.setItem('auth_user', JSON.stringify(user));
        storage.setItem('auth_last_activity', Date.now().toString());
        
        if (credentials.rememberMe) {
          localStorage.setItem('auth_remember_me', 'true');
        }

        return { success: true, token, user, session };
      }

      throw new Error(response.data.message || 'Admin login failed');
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Admin login failed';
      return { success: false, error: message };
    }
  },

  async getCurrentUser() {
    try {
      const response = await httpClient.get('/auth/me');
      
      if (response.data.success && response.data.data) {
        const user = response.data.data;
        
        // Update stored user data
        const storage = localStorage.getItem('auth_token') ? localStorage : sessionStorage;
        storage.setItem('auth_user', JSON.stringify(user));
        
        return { success: true, user };
      }

      throw new Error('Failed to get current user');
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to get user data';
      return { success: false, error: message };
    }
  },

  async logout() {
    try {
      await httpClient.post('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      clearAuthData();
    }
  },

  async refreshToken() {
    try {
      const response = await httpClient.post('/auth/sessions/extend', {
        location: await getCurrentLocation().catch(() => null)
      });

      if (response.data.success && response.data.data?.token) {
        const { token, user } = response.data.data;
        
        // Update stored data
        const storage = localStorage.getItem('auth_token') ? localStorage : sessionStorage;
        storage.setItem('auth_token', token);
        storage.setItem('auth_user', JSON.stringify(user));
        storage.setItem('auth_last_activity', Date.now().toString());

        return { success: true, token, user };
      }

      throw new Error('Token refresh failed');
    } catch (error) {
      clearAuthData();
      return { success: false, error: error.message };
    }
  }
};

export default httpClient;